import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import { useOrganizationWageSetMutationHandlers } from "../hooks/use-organization-wage-set-mutations";
import { getTodayInTimezone, isCalendarDate } from "../utils/wage-dates";
import {
  wageFormValuesFromWages,
  wageFormValuesToInput,
  validateWageForm,
  type OrganizationWageSetWages,
  type WageFormErrors,
} from "../utils/wage-fields";
import { DateField } from "./date-field";
import { OrganizationWageSetDialog } from "./organization-wage-set-dialog";
import { WageFields } from "./wage-fields";

type ChangeWagesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationWageSetId: string;
  currentVersionId: string;
  currentWages: OrganizationWageSetWages;
};

export function ChangeWagesDialog({
  open,
  onOpenChange,
  organizationWageSetId,
  currentVersionId,
  currentWages,
}: ChangeWagesDialogProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { invalidateOrganizationWageSets, handleError } =
    useOrganizationWageSetMutationHandlers();
  const [startsOn, setStartsOn] = useState(() =>
    getTodayInTimezone({ timezoneId: selectedOrgBusinessUnit.timezoneId }),
  );
  const [wages, setWages] = useState(() =>
    wageFormValuesFromWages(currentWages),
  );
  const [wageErrors, setWageErrors] = useState<WageFormErrors>({});
  const [dateError, setDateError] = useState<string>();

  const addVersionMutation = useMutation(
    trpc.organizationWageSet.addVersion.mutationOptions({
      onSuccess: async () => {
        await invalidateOrganizationWageSets();
        toast.success("Wages updated");
        onOpenChange(false);
      },
      onError: handleError,
    }),
  );

  function handleSubmit() {
    const nextWageErrors = validateWageForm(wages);
    const nextDateError = isCalendarDate(startsOn)
      ? undefined
      : "Choose a start date";
    setWageErrors(nextWageErrors);
    setDateError(nextDateError);
    if (nextDateError || Object.keys(nextWageErrors).length > 0) {
      return;
    }
    addVersionMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      organizationWageSetId,
      startsOn,
      wages: wageFormValuesToInput(wages),
      expectedCurrentVersionId: currentVersionId,
    });
  }

  return (
    <OrganizationWageSetDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Change wages"
      description="The new wages start on the date you choose, and the current wages close the day before."
      submitLabel="Save new wages"
      isPending={addVersionMutation.isPending}
      onSubmit={handleSubmit}
    >
      <DateField
        label="Starts on"
        value={startsOn}
        onChange={setStartsOn}
        error={dateError}
        hint="Must be after the current wages' start date."
      />
      <WageFields values={wages} errors={wageErrors} onChange={setWages} />
    </OrganizationWageSetDialog>
  );
}
